import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faBan, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Spinner from "react-activity/dist/Spinner";
import "react-activity/dist/Spinner.css";
import { apiUrl } from "../../../config/Config";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [filter, setFilter] = useState("all");
  const [rowData, setRowData] = useState([]);
  const [showloding, setshowloding] = useState(false);
  const paginationPageSizeSelector = useMemo(() => [10, 20, 50, 100], []);

  const headers = useMemo(() => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  }), [userdata?.token]);

  const handleInvalidUser = useCallback((message) => {
    if (message === "invalid user") {
      dispatch({ type: "userdata", payload: {} });
      navigate("/admin/login");
      return true;
    }
    return false;
  }, [dispatch, navigate]);

  const fetchUsers = useCallback(async () => {
    setshowloding(true);
    await axios
      .get(apiUrl + "admingetusers?status=" + filter, headers)
      .then((res) => {
        if (res.data.status === "success") {
          setRowData(res.data.data || []);
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => alert(err.message));
    setshowloding(false);
  }, [filter, handleInvalidUser, headers]);

  const updateUserStatus = async (id, status) => {
    const result = window.confirm(status === "banned" ? "Ban this user?" : "Activate this user?");
    if (!result) return;

    setshowloding(true);
    await axios
      .post(apiUrl + "adminupdateuserstatus", { id, status }, headers)
      .then((res) => {
        if (res.data.status === "success") {
          fetchUsers();
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => alert(err.message));
    setshowloding(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const columns = [
    { field: "fullname", headerName: "Name", minWidth: 180 },
    { field: "email", headerName: "Email", minWidth: 220 },
    { field: "mobile", headerName: "Mobile", minWidth: 140 },
    { field: "designation", headerName: "Designation", minWidth: 150 },
    { field: "city", headerName: "City", minWidth: 130 },
    { field: "state", headerName: "State", minWidth: 130 },
    { field: "posts_count", headerName: "Posts", minWidth: 100 },
    { field: "pending_reports_count", headerName: "Reports", minWidth: 105 },
    { field: "blocked_by_count", headerName: "Blocked By", minWidth: 120 },
    {
      field: "created_at",
      headerName: "Joined",
      minWidth: 150,
      cellRenderer: (props) => props.data.created_at ? moment(props.data.created_at).format("DD MMM YYYY") : "-",
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      cellRenderer: (props) => (
        <span className={props.data.status === "banned" || props.data.status === "false" ? "admin-status-pill danger" : "admin-status-pill"}>
          {props.data.status === "banned" ? "Banned" : props.data.status === "false" ? "Inactive" : "Active"}
        </span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 150,
      cellRenderer: (props) => (
        <div className="admin-grid-actions">
          <button title="View details" onClick={() => navigate("/admin/user-details/" + props.data.id)}>
            <FontAwesomeIcon icon={faEye} />
          </button>
          {props.data.status === "banned" ? (
            <button title="Activate user" onClick={() => updateUserStatus(props.data.id, "true")}>
              <FontAwesomeIcon icon={faRotateLeft} />
            </button>
          ) : (
            <button title="Ban user" className="danger" onClick={() => updateUserStatus(props.data.id, "banned")}>
              <FontAwesomeIcon icon={faBan} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="dashboardbox" style={{ width: "100%" }}>
      <div className="productstogglebar mt-2">
        <div className="orderstatustogglediv">
          {[
            ["all", "All Users"],
            ["inactive", "Inactive"],
            ["blocked", "Blocked"],
            ["reported", "Reported"],
          ].map(([key, label]) => (
            <div key={key} className={filter === key ? "orderstatusdiv active" : "orderstatusdiv"} onClick={() => setFilter(key)}>
              <p>{label}</p>
            </div>
          ))}
        </div>
        {showloding ? <div className="moderation-loader"><Spinner color="#34A853" size={18} /></div> : null}
      </div>

      <div className="ag-theme-quartz mt-4" style={{ width: "100%", height: 560 }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columns}
          defaultColDef={{ flex: 1, filter: true, sortable: true, resizable: true }}
          suppressRowClickSelection={true}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={paginationPageSizeSelector}
          rowHeight={54}
        />
      </div>
    </div>
  );
};

export default Users;
